import React from "react";
import { createChatClient } from "../../lib/chatApi";
import { SimpleSearchProvider, useSimpleSearch } from "../../context/SimpleSearchContext";
import { ProductCandidatesGrid } from "../../components/ProductCandidatesGrid";
import { SimpleSearchRefinements } from "../../components/SimpleSearchRefinements";
import { SearchInput } from "../../components/ChatInput";
import type { Product } from "../../lib/mockData";
import { events, EVENT_NAVIGATE_PRODUCT } from "../events";
import { Plus, RotateCcw, X } from "lucide-react";
import { fetchWidgetInit, getBackendMode, getWidgetOrigins } from "../widgetInit";
import {
  getCachedWidgetInit,
  getWidgetInitCacheKey,
  setCachedWidgetInit,
} from "../widgetInitCache";
import { readStoredTestSessionId } from "../config";
import { useLocale } from "../../context/LocaleContext";
import { storeAttribution } from "../../lib/attribution";

interface SimpleSearchWidgetProps {
  storeId: string;
  apiBase: string;
  initialQuery?: string;
  onRetry?: () => void;
  onClose?: () => void;
  removePadding?: boolean;
}

export function SimpleSearchWidget({
  storeId,
  apiBase,
  initialQuery,
  onRetry,
  onClose,
  removePadding = false,
}: SimpleSearchWidgetProps) {
  const refreshToken = React.useCallback(async () => {
    const MODE = getBackendMode();
    const { initUrl } = getWidgetOrigins(MODE);
    const cacheKey = getWidgetInitCacheKey({ storeId, mode: MODE, initUrl });
    const cached = getCachedWidgetInit(cacheKey);
    if (cached) return cached;
    const refreshed = await fetchWidgetInit(storeId, MODE);
    if (!refreshed) return null;
    setCachedWidgetInit(cacheKey, refreshed.token, refreshed.apiBase);
    return refreshed;
  }, [storeId]);

  const client = React.useMemo(
    () => createChatClient({ storeId, apiBase, refreshToken }),
    [storeId, apiBase, refreshToken]
  );
  const testSessionId = readStoredTestSessionId() || (window as any).__WW__?.testSessionId;

  return (
    <SimpleSearchProvider client={client} minimal={false} testSessionId={testSessionId}>
      <SimpleSearchInner
        storeId={storeId}
        initialQuery={initialQuery}
        onRetry={onRetry}
        onClose={onClose}
        removePadding={removePadding}
      />
    </SimpleSearchProvider>
  );
}

interface SimpleSearchInnerProps {
  storeId: string;
  initialQuery?: string;
  onRetry?: () => void;
  onClose?: () => void;
  removePadding?: boolean;
}

type SimpleSearchTopDisplayMode = "default" | "oj";

function parseThemeStringToken(rawValue: string): string {
  const trimmed = rawValue.trim();
  if (!trimmed) return "";
  const hasMatchingQuotes =
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"));
  if (hasMatchingQuotes) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
}

function SimpleSearchInner({
  storeId,
  initialQuery,
  onRetry,
  onClose,
  removePadding = false,
}: SimpleSearchInnerProps) {
  const { t } = useLocale();
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [showRefinementsUi, setShowRefinementsUi] = React.useState(false);
  const [topDisplayMode, setTopDisplayMode] = React.useState<SimpleSearchTopDisplayMode>("default");
  const hideRefinementsUi = !showRefinementsUi;
  const [desktopPanelOpen, setDesktopPanelOpen] = React.useState(false);
  const hasRefinementsRail = !hideRefinementsUi && desktopPanelOpen;
  const desktopCols = hasRefinementsRail ? 3 : 4;
  const desktopBatchSize = hasRefinementsRail ? 6 : 8;
  const {
    products,
    queryTitle,
    resultsCount,
    loading,
    loadingMore,
    error,
    hasMore,
    availableFilters,
    activeFilters,
    activeSort,
    search,
    fetchMore,
    toggleFilter,
    setRangeFilter,
    setSort,
    onProductClick,
  } =
    useSimpleSearch();
  const [panelMode, setPanelMode] = React.useState<"filter" | "sort">("filter");
  const [mobilePanelOpen, setMobilePanelOpen] = React.useState(false);
  const filterToggleLabel = t("simple_search.filter_toggle");
  const sortToggleLabel = t("simple_search.sort_toggle");
  const handleDesktopToggle = React.useCallback((mode: "filter" | "sort") => {
    if (desktopPanelOpen && panelMode === mode) {
      setDesktopPanelOpen(false);
      return;
    }
    setPanelMode(mode);
    setDesktopPanelOpen(true);
  }, [desktopPanelOpen, panelMode]);
  const handleMobileToggle = React.useCallback((mode: "filter" | "sort") => {
    if (mobilePanelOpen && panelMode === mode) {
      setMobilePanelOpen(false);
      return;
    }
    setPanelMode(mode);
    setMobilePanelOpen(true);
  }, [mobilePanelOpen, panelMode]);
  const showNoResults =
    !loading &&
    !error &&
    !!queryTitle &&
    products.length === 0 &&
    typeof resultsCount === "number" &&
    resultsCount === 0;
  const isOjTopDisplay = topDisplayMode === "oj";
  const ojQueryText = (queryTitle || t("common.results")).trim();
  const ojResultsCount =
    typeof resultsCount === "number" ? resultsCount : products.length;
  const ojSummaryTemplate = t("simple_search.oj_summary");
  const [ojBeforeCount, ojAfterCountRaw = ""] = ojSummaryTemplate.split("{count}");
  const [ojBetweenCountAndQuery, ojAfterQuery = ""] = ojAfterCountRaw.split("{query}");

  // On mount: trigger search if initial query provided
  React.useEffect(() => {
    const q = (initialQuery || "").trim();
    if (q) {
      search(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const readDisplayModes = () => {
      if (!rootRef.current) return;
      const styles = window.getComputedStyle(rootRef.current);
      const refinementsDisplay = parseThemeStringToken(
        styles.getPropertyValue("--ww-simple-search-refinements-display")
      ).toLowerCase();
      setShowRefinementsUi(refinementsDisplay !== "" && refinementsDisplay !== "none");

      const topDisplayRaw = parseThemeStringToken(
        styles.getPropertyValue("--ww-simple-search-top-display-mode")
      ).toLowerCase();
      setTopDisplayMode(topDisplayRaw === "oj" ? "oj" : "default");
    };

    // Theme CSS may be applied asynchronously.
    readDisplayModes();
    const rafId = window.requestAnimationFrame(readDisplayModes);
    const timeoutId = window.setTimeout(readDisplayModes, 200);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, []);

  const handleProductSelect = React.useCallback((product: Product) => {
    if (!product.productUrl) return;
    onProductClick();
    storeAttribution({
      storeId,
      source: "browser_read_more",
      productId: product.id,
    });
    events.emit(EVENT_NAVIGATE_PRODUCT, {
      product: {
        id: product.id,
        title: product.title,
        images: product.images,
        price: product.price,
        productUrl: product.productUrl,
      },
      target: "_self",
    });
  }, [onProductClick, storeId]);

  const handleOverlaySearch = React.useCallback(
    async (value: string) => {
      const nextQuery = (value || "").trim();
      if (!nextQuery) return;
      const baseQuery = (queryTitle || "").trim();
      const combinedQuery = baseQuery ? `${baseQuery} , ${nextQuery}` : nextQuery;
      await search(combinedQuery);
    },
    [queryTitle, search]
  );

  return (
    <div
      ref={rootRef}
      className="ww-simple-search h-full w-full flex flex-col relative"
      style={{ backgroundColor: "var(--color-background)" }}
    >
      {/* Top bar: retry / close buttons */}
      <div className={`flex items-center justify-between ${removePadding ? "p-0" : "p-[12px]"}`}>
        <button
          type="button"
          className="ww-icon-btn w-[36px] h-[36px] flex items-center justify-center rounded-full border"
          aria-label={t("common.retry")}
          onClick={onRetry}
          title={t("common.retry")}
        >
          <RotateCcw className="w-[20px] h-[20px]" />
        </button>
        {onClose ? (
          <button
            type="button"
            className="ww-icon-btn w-[36px] h-[36px] flex items-center justify-center rounded-full border"
            aria-label={t("common.close")}
            onClick={onClose}
            title={t("common.close")}
          >
            <X className="w-[24px] h-[24px]" />
          </button>
        ) : (
          <div className="w-[36px]" />
        )}
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-y-auto">
        <div className={`max-w-[1200px] mx-auto ${removePadding ? "pb-0" : "pb-36"}`}>
          {showNoResults ? (
            <div
              className={`${removePadding ? "px-0 pt-0 pb-0" : "px-4 pt-6 pb-8"} flex flex-col items-center text-center`}
            >
              <p
                className="text-[16px]"
                style={{ color: "var(--text-secondary)", fontFamily: "var(--font-body)" }}
              >
                {t("simple_search.no_results")}
              </p>
            </div>
          ) : (
            <>
              {/* Header */}
              {isOjTopDisplay ? (
                <div className={`${removePadding ? "px-0" : "px-4"} mb-4`}>
                  <div className="flex items-start justify-between gap-3">
                    <span
                      className="text-[16px] text-left"
                      style={{ color: "var(--text-secondary)", fontFamily: "var(--font-body)" }}
                    >
                      {ojBeforeCount}
                      <strong>{ojResultsCount}</strong>
                      {ojBetweenCountAndQuery}
                      <strong>{ojQueryText}</strong>
                      {ojAfterQuery}
                    </span>
                    {!hideRefinementsUi && (
                      <div className="hidden lg:flex lg:items-center lg:gap-2">
                        <button
                          type="button"
                          className={`ww-simple-search-refinements__toggle ${desktopPanelOpen && panelMode === "filter" ? "is-active" : ""}`}
                          onClick={() => handleDesktopToggle("filter")}
                          aria-pressed={desktopPanelOpen && panelMode === "filter"}
                        >
                          <span>{filterToggleLabel}</span>
                          <Plus className="ww-simple-search-refinements__toggle-icon" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className={`ww-simple-search-refinements__toggle ${desktopPanelOpen && panelMode === "sort" ? "is-active" : ""}`}
                          onClick={() => handleDesktopToggle("sort")}
                          aria-pressed={desktopPanelOpen && panelMode === "sort"}
                        >
                          <span>{sortToggleLabel}</span>
                          <Plus className="ww-simple-search-refinements__toggle-icon" aria-hidden="true" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className={`flex flex-col gap-1 ${removePadding ? "px-0" : "px-4"}`}>
                  <h2
                    className="text-[36px] text-left font-semibold"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {queryTitle || t("common.results")}
                  </h2>
                  {typeof resultsCount === "number" && resultsCount > 0 && (
                    <span
                      className="text-[14px] text-left"
                      style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-body)" }}
                    >
                      {resultsCount} {t("common.hits")}
                    </span>
                  )}
                </div>
              )}

              {/* Error display */}
              {error && (
                <div className={`${removePadding ? "px-0 pt-0" : "px-4 pt-3"}`}>
                  <div className="ww-alert ww-alert-error" role="alert">
                    {error}
                  </div>
                </div>
              )}

              {/* Product grid */}
              <section className="px-0">
                {!hideRefinementsUi && (
                  <div className="lg:hidden mb-4">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="ww-simple-search-refinements__toggle"
                        onClick={() => handleMobileToggle("filter")}
                      >
                        <span>{filterToggleLabel}</span>
                        <Plus className="ww-simple-search-refinements__toggle-icon" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="ww-simple-search-refinements__toggle"
                        onClick={() => handleMobileToggle("sort")}
                      >
                        <span>{sortToggleLabel}</span>
                        <Plus className="ww-simple-search-refinements__toggle-icon" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Desktop: Filter/Sort buttons above the grid */}
                {!hideRefinementsUi && !isOjTopDisplay && (
                  <div className="hidden lg:flex lg:items-center lg:gap-2 lg:mb-4">
                    <button
                      type="button"
                      className={`ww-simple-search-refinements__toggle ${desktopPanelOpen && panelMode === "filter" ? "is-active" : ""}`}
                      onClick={() => handleDesktopToggle("filter")}
                      aria-pressed={desktopPanelOpen && panelMode === "filter"}
                    >
                      <span>{filterToggleLabel}</span>
                      <Plus className="ww-simple-search-refinements__toggle-icon" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className={`ww-simple-search-refinements__toggle ${desktopPanelOpen && panelMode === "sort" ? "is-active" : ""}`}
                      onClick={() => handleDesktopToggle("sort")}
                      aria-pressed={desktopPanelOpen && panelMode === "sort"}
                    >
                      <span>{sortToggleLabel}</span>
                      <Plus className="ww-simple-search-refinements__toggle-icon" aria-hidden="true" />
                    </button>
                  </div>
                )}

                {/* Desktop: refinements panel and product grid top-aligned beneath buttons */}
                <div
                  className={hideRefinementsUi ? "hidden lg:block" : "hidden lg:grid lg:grid-cols-4 lg:items-start"}
                  style={hideRefinementsUi ? undefined : { gap: "var(--product-grid-gap)" }}
                >
                  {hasRefinementsRail && (
                    <aside className="ww-simple-search-refinements-rail lg:order-2">
                      <SimpleSearchRefinements
                        mode={panelMode}
                        onModeChange={setPanelMode}
                        availableFilters={availableFilters}
                        activeFilters={activeFilters}
                        activeSort={activeSort}
                        onToggleFilter={toggleFilter}
                        onRangeChange={setRangeFilter}
                        onSortChange={setSort}
                        loading={loading}
                        hideToggleButtons
                      />
                    </aside>
                  )}
                  <div
                    className={
                      hideRefinementsUi
                        ? ""
                        : hasRefinementsRail
                          ? "lg:col-span-3 lg:order-1"
                          : "lg:col-span-4 lg:order-1"
                    }
                  >
                    {loading && products.length === 0 ? (
                      <div className="mt-4" style={{ fontFamily: "var(--font-body)" }}>
                        <div
                          className={`grid ${desktopCols === 3 ? "grid-cols-3" : "grid-cols-4"}`}
                          style={{ gap: "var(--product-grid-gap)" }}
                        >
                          {Array.from({ length: 8 }).map((_, i) => (
                            <div
                              key={`sk-${i}`}
                              className="aspect-[3/4] ww-skeleton-shimmer"
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <ProductCandidatesGrid
                        products={products}
                        onProductSelect={handleProductSelect}
                        initialMobile={4}
                        initialTablet={6}
                        initialDesktop={desktopBatchSize}
                        stepMobile={4}
                        stepTablet={6}
                        stepDesktop={desktopBatchSize}
                        hasMore={hasMore}
                        isLoadingMore={loadingMore}
                        totalCount={resultsCount}
                        onLoadMore={fetchMore}
                        desktopCols={desktopCols}
                        paginationMode="infinite"
                      />
                    )}
                  </div>
                </div>

                <div className="lg:hidden">
                  {loading && products.length === 0 ? (
                    <div className="mt-4">
                      <div
                        className="grid grid-cols-2 md:grid-cols-3"
                        style={{ gap: "var(--product-grid-gap)" }}
                      >
                        {Array.from({ length: 8 }).map((_, i) => (
                          <div
                            key={`sk-${i}`}
                            className="aspect-[3/4] ww-skeleton-shimmer"
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <ProductCandidatesGrid
                      products={products}
                      onProductSelect={handleProductSelect}
                      initialMobile={4}
                      initialTablet={6}
                      initialDesktop={8}
                      stepMobile={4}
                      stepTablet={6}
                      stepDesktop={8}
                      hasMore={hasMore}
                      isLoadingMore={loadingMore}
                      totalCount={resultsCount}
                      onLoadMore={fetchMore}
                      paginationMode="infinite"
                    />
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      <div
        className="absolute inset-x-0 bottom-0 z-20 transition-[bottom] duration-200 ease-out focus-within:bottom-[88px] md:focus-within:bottom-0"
        style={{ backgroundColor: "rgba(255, 255, 255, 0.55)", backdropFilter: "blur(4px)" }}
      >
        <div className={`max-w-[1200px] mx-auto ${removePadding ? "px-0 pt-0 pb-0" : "px-4 pt-2 pb-3"}`}>
          <SearchInput
            placeholder={t("search.placeholder")}
            selectedOptions={[]}
            onRemoveOption={() => {}}
            onSubmit={(text) => {
              handleOverlaySearch(text);
            }}
            disabled={loading}
          />
        </div>
      </div>

      {!hideRefinementsUi && mobilePanelOpen && (
        <div className="lg:hidden absolute inset-0 z-40">
          <button
            type="button"
            className="absolute inset-0 bg-black/20"
            onClick={() => setMobilePanelOpen(false)}
            aria-label={t("common.close")}
          />
          <div className="absolute top-0 w-[100vw] right-0 h-full bg-[var(--color-background)] border-l shadow-xl">
            <div className="h-full flex flex-col p-4">
              <div className="flex items-center justify-between pb-3 border-b">
                <h3 className="text-[20px]" style={{ fontFamily: "var(--font-heading)" }}>
                  {panelMode === "filter" ? t("simple_search.filter") : t("simple_search.sort")}
                </h3>
                <button
                  type="button"
                  className="ww-icon-btn w-[32px] h-[32px] flex items-center justify-center rounded-full border"
                  onClick={() => setMobilePanelOpen(false)}
                  aria-label={t("common.close")}
                >
                  <X className="w-[16px] h-[16px]" />
                </button>
              </div>
              <div className="flex-1 pt-2 min-h-0 overflow-y-auto">
                <SimpleSearchRefinements
                  mode={panelMode}
                  onModeChange={setPanelMode}
                  availableFilters={availableFilters}
                  activeFilters={activeFilters}
                  activeSort={activeSort}
                  onToggleFilter={toggleFilter}
                  onRangeChange={setRangeFilter}
                  onSortChange={setSort}
                  loading={loading}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
